import { toast } from "@/components/hooks/use-toast";
import StorageService from '@/lib/storage';
import type { Document } from '@/types';
import type { FileIconType } from '@/lib/icons';

export const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB
export const DEFAULT_MAX_DOCUMENTS = 5;

export const ALLOWED_MIME_TYPES = [
    // Document formats
    'application/pdf',

    // Office formats — parsed deterministically via AILANG Parse on the backend
    // (backend/tools/ailang_parse_client.py). Preserves tables, merged cells,
    // and track changes without burning LLM tokens.
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.oasis.opendocument.text', // .odt
    'application/vnd.oasis.opendocument.presentation', // .odp
    'application/vnd.oasis.opendocument.spreadsheet', // .ods
    'application/epub+zip', // .epub

    // Programming languages
    'text/javascript',
    'text/x-python',

    // Text formats
    'text/plain',
    'text/html',
    'text/css',
    'text/md',
    'text/csv',
    'text/xml',
    'text/rtf',

    // Images
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',

    // Audio
    'audio/wav',
    'audio/mp3',
    'audio/mpeg',

    // Video
    'video/mp4',
    'video/mpeg',
    'video/mov',
    'video/avi',
    'video/wmv',
    'video/flv'
] as const;

export const ACCEPTED_FILE_TYPES = [
    // Documents
    ".pdf",

    // Office (AILANG Parse deterministic extraction)
    ".docx", ".pptx", ".xlsx", ".odt", ".odp", ".ods", ".epub",

    // Programming
    ".js", ".py",

    // Text
    ".txt", ".html", ".css", ".md", ".csv", ".xml", ".rtf", ".json",

    // Images
    ".png", ".jpeg", ".jpg", ".gif", ".webp",

    // Audio
    ".wav", ".mp3", ".mpeg",

    // Video
    ".mp4", ".mpeg", ".mov", ".avi", ".wmv", ".flv", ".mpg"
].join(',');

export class DocumentHandler {
  static validateFileSize(file: File): void {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(
        `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`
      );
    }
  }

  static validateDocumentCount(currentCount: number, maxDocuments: number = DEFAULT_MAX_DOCUMENTS): void {
    if (currentCount >= maxDocuments) {
      throw new Error(
        `Maximum document limit of ${maxDocuments} reached (${currentCount}/${maxDocuments}). Please remove some documents before uploading more.`
      );
    }
  }

  static validateFileType(file: File): void {
    const fileType = file.type;
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;

    if (extension === '.mp3' && (fileType === 'audio/mpeg' || fileType === 'audio/mp3')) {
      return; // Valid MP3 file
    }

    if (!ALLOWED_MIME_TYPES.includes(fileType as typeof ALLOWED_MIME_TYPES[number]) &&
        !ACCEPTED_FILE_TYPES.split(',').includes(extension)) {
      throw new Error(
        `File type not allowed. Allowed types are: ${ACCEPTED_FILE_TYPES}`
      );
    }
  }

  static getCanonicalMimeType(file: File): string {
    // For tracking/logging purposes, we can normalize the MIME type
    if (file.type === 'audio/mpeg' && file.name.toLowerCase().endsWith('.mp3')) {
      return 'audio/mp3';
    }
    return file.type;
  }

  static async handleFileUpload(
    event: React.ChangeEvent<HTMLInputElement>,
    options: {
      userId: string;
      assistantId: string;
      onUploadSuccess?: (doc: Document) => void;
      currentDocuments?: Document[];
      maxDocuments?: number;
    }
  ): Promise<Document | undefined> {
    const { 
      userId, 
      assistantId, 
      onUploadSuccess, 
      currentDocuments = [],
      maxDocuments = DEFAULT_MAX_DOCUMENTS
    } = options;
    
    const file = event.target.files?.[0];
    
    if (!file || !userId || !assistantId) {
      throw new Error('Missing required upload parameters');
    }

    try {
      this.validateFileSize(file);
      this.validateFileType(file);
      this.validateDocumentCount(currentDocuments.length, maxDocuments);
      
      const canonicalType = this.getCanonicalMimeType(file);
      console.log(`Uploading file with type: ${canonicalType}`);
      
      const document = await StorageService.uploadDocument(file, userId, assistantId);
      
      if (onUploadSuccess) {
        onUploadSuccess(document);
      }

      toast({
        title: "Success",
        description: `${file.name} has been uploaded successfully (${currentDocuments.length + 1}/${maxDocuments})`
      });

      return document;
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload file",
        variant: "destructive"
      });
      throw error;
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  }

  static async handleDeleteDocument(
    document: Document,
    index: number,
    options: {
      onDeleteSuccess?: (index: number) => void;
      currentDocuments?: Document[];
      maxDocuments?: number;
    }
  ): Promise<void> {
    const { onDeleteSuccess, currentDocuments = [], maxDocuments = DEFAULT_MAX_DOCUMENTS } = options;

    if (!document.storagePath) {
      throw new Error('Cannot delete document: missing storage path');
    }

    try {
      await StorageService.deleteDocument(document.storagePath);
      
      if (onDeleteSuccess) {
        onDeleteSuccess(index);
      }

      toast({
        title: "Success",
        description: `${document.name} has been deleted (${currentDocuments.length - 1}/${maxDocuments})`
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete file",
        variant: "destructive"
      });
      throw error;
    }
  }

  static getFileIcon(type: string): FileIconType {
    if (type.startsWith('image/')) return 'Image';
    if (type.startsWith('video/')) return 'Video';
    if (type.startsWith('audio/')) return 'Music';
    
    const cleanType = type.toLowerCase().replace('application/', '').replace('text/', '').replace('.', '');
    
    const typeToIcon: Record<string, FileIconType> = {
      // Documents
      'pdf': 'FileText',
      'rtf': 'FileText',
      'txt': 'FileText',
      'md': 'FileText',
      'markdown': 'FileText',
      'plain': 'FileText',
      
      // Microsoft Office
      'vnd.openxmlformats-officedocument.wordprocessingml.document': 'FileText',
      'msword': 'FileText',
      'doc': 'FileText',
      'docx': 'FileText',
      'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'FileSpreadsheet',
      'vnd.ms-excel': 'FileSpreadsheet',
      'xls': 'FileSpreadsheet',
      'xlsx': 'FileSpreadsheet',
      'vnd.openxmlformats-officedocument.presentationml.presentation': 'FileText',
      'vnd.ms-powerpoint': 'FileText',
      'ppt': 'FileText',
      'pptx': 'FileText',
      
      // Programming
      'javascript': 'FileCode',
      'js': 'FileCode',
      'typescript': 'FileCode',
      'ts': 'FileCode',
      'python': 'FileCode',
      'py': 'FileCode',
      'html': 'FileCode',
      'css': 'FileCode',
      'xml': 'FileCode',
      'json': 'FileCode',
      
      // Data
      'csv': 'FileSpreadsheet',
      'spreadsheet': 'FileSpreadsheet',
      'excel': 'FileSpreadsheet',
      
      // Images
      'png': 'Image',
      'jpeg': 'Image',
      'jpg': 'Image',
      'webp': 'Image',
      'heic': 'Image',
      'heif': 'Image',
      'svg': 'Image',
      'gif': 'Image',
      
      // Audio
      'wav': 'Music',
      'mp3': 'Music',
      'aiff': 'Music',
      'aac': 'Music',
      'ogg': 'Music',
      'flac': 'Music',
      
      // Video
      'mp4': 'Video',
      'mpeg': 'Video',
      'mov': 'Video',
      'avi': 'Video',
      'flv': 'Video',
      'mpg': 'Video',
      'webm': 'Video',
      'wmv': 'Video',
      '3gpp': 'Video'
    };

    return typeToIcon[cleanType] || 'File';
  }
}